import { supabase } from '../lib/supabase';

/**
 * Notification Service - Maneja las operaciones de notificaciones
 */

export const notificationService = {
  /**
   * Obtener notificaciones del usuario
   * @param {string} userId - ID del usuario
   */
  async getNotifications(userId) {
    const { data, error } = await supabase
      .from('notifications')
      .select(`
        *,
        actor:actor_id (
          id,
          display_name,
          username,
          avatar_url
        )
      `)
      .eq('recipient_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }

    return data?.map(n => ({
      ...n,
      actor_name: n.actor?.display_name || 'Chef',
      actor_username: n.actor?.username || '',
      actor_avatar: n.actor?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${n.actor_id}`
    })) || [];
  },

  /**
   * Marcar notificación como leída
   * @param {string} notificationId - ID de la notificación
   */
  async markAsRead(notificationId) {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId);

    if (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
    return true;
  },

  /**
   * Marcar todas las notificaciones como leídas
   * @param {string} userId - ID del usuario
   */
  async markAllAsRead(userId) {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('recipient_id', userId)
      .eq('is_read', false);

    if (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
    return true;
  },

  /**
   * Eliminar notificación
   * @param {string} notificationId - ID de la notificación
   */
  async deleteNotification(notificationId) {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);

    if (error) {
      console.error('Error deleting notification:', error);
      return false;
    }
    return true;
  },

  /**
   * Obtener conteo de notificaciones no leídas
   * @param {string} userId - ID del usuario
   */
  async getUnreadCount(userId) {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('recipient_id', userId)
      .eq('is_read', false);

    if (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
    return count || 0;
  },

  /**
   * Suscribirse a nuevas notificaciones en tiempo real
   * @param {string} userId - ID del usuario
   * @param {function} onNewNotification - Callback cuando llega una notificación
   */
  subscribeToNotifications(userId, onNewNotification) {
    const subscription = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${userId}`
        },
        (payload) => {
          if (onNewNotification) {
            onNewNotification(payload.new);
          }
        }
      )
      .subscribe();

    return subscription;
  },

  /**
   * Desuscribirse de notificaciones
   * @param {object} subscription - Objeto de suscripción
   */
  unsubscribe(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  }
};

export default notificationService;
